import { PENDING_MESSAGE_DAYS } from '@crm/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Loader2, MessageSquare } from 'lucide-react';
import { describeError } from '../../lib/api.ts';
import { api } from '../../lib/client.ts';
import { BlockCard } from '../records/block-card.tsx';
import { EmptyState } from '../ui/empty-state.tsx';
import { cn } from '../../lib/utils.ts';
import { RAIL, type Urgency } from '../ui/urgency-rail.ts';
import { ago } from './time.ts';

const DAY_MS = 24 * 60 * 60_000;

/** How long the client has waited, as the rail says it: a day, then 3 days. */
function waiting(lastMessageAt: string, now: number): Urgency | null {
  const waited = now - Date.parse(lastMessageAt);
  if (waited >= 3 * DAY_MS) return 'danger';
  if (waited >= DAY_MS) return 'warning';
  return null;
}

/**
 * Home's Pending messages: text conversations on my projects where the client wrote last, in
 * the last 14 days. One opens on its project's Messages tab; the conversation travels in the
 * router's history state, because its key holds the client's phone number.
 */
export function PendingMessagesCard({ fill = false }: { fill?: boolean }) {
  const pending = useQuery({ queryKey: ['messages', 'pending'], queryFn: api.messages.pending });
  const items = pending.data?.items ?? [];
  const now = Date.now();
  return (
    <BlockCard title="Pending messages" count={pending.data ? items.length : undefined} fill={fill}>
      {() =>
        pending.isPending ? (
          <p className="text-muted-foreground flex items-center gap-2 text-sm">
            <Loader2 className="size-4 animate-spin" aria-hidden /> Loading…
          </p>
        ) : pending.error ? (
          <p className="text-danger text-sm" role="alert">
            {describeError(pending.error)}
          </p>
        ) : items.length === 0 ? (
          <EmptyState
            icon={MessageSquare}
            title="No client is waiting for a reply"
            description={`Texts on your projects that nobody answered in the last ${PENDING_MESSAGE_DAYS} days show here.`}
            data-testid="home-no-pending"
            className="h-full"
          />
        ) : (
          <ul className="divide-border divide-y" data-testid="home-pending-messages">
            {items.map((m) => {
              const wait = waiting(m.lastMessageAt, now);
              return (
                <li
                  key={m.conversation}
                  className={wait ? RAIL[wait] : undefined}
                  data-wait={wait ?? undefined}
                >
                  <Link
                    to="/projects/$id"
                    params={{ id: m.project.id }}
                    search={{ tab: 'messages' }}
                    state={{ conversation: m.conversation }}
                    className="hover:bg-muted/60 block rounded-md py-2 pr-2 pl-3"
                  >
                    <span className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-sm font-medium">
                        {m.contactNames.join(', ') || m.clientNumber || 'Client'}
                      </span>
                      <span
                        className={cn(
                          'shrink-0 text-xs',
                          wait === 'danger'
                            ? 'text-danger font-medium'
                            : wait === 'warning'
                              ? 'text-state-warning-fg font-medium'
                              : 'text-muted-foreground',
                        )}
                      >
                        {ago(m.lastMessageAt)}
                      </span>
                    </span>
                    <span className="text-muted-foreground block truncate text-xs">
                      {m.project.name}
                    </span>
                    {m.lastMessagePreview && (
                      <span className="block truncate text-sm">{m.lastMessagePreview}</span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        )
      }
    </BlockCard>
  );
}
